import { type ReactNode } from 'react';

import { Link, useMatches, type UIMatch } from 'react-router-dom';

import { AuthStatus } from '@/authentication/auth-status';
import { cn } from '@/lib/utils';

import { SidebarControl } from '../sidebar/sidebarControl';
import { Separator } from '../ui/separator';
import { useSidebar } from '../ui/sidebar';

// data is undefined when the loader threw (a 403 from requirePermissions), so a
// crumb falls back on params.
type CrumbMatch<TData> = UIMatch<TData | undefined>;

export interface CrumbHandle<TData = unknown> {
  crumb: (match: CrumbMatch<TData>) => ReactNode;
  /** Link target, match.pathname by default; false when the crumb is interactive itself. */
  to?: string | false | ((match: CrumbMatch<TData>) => string);
  /** Rendered at the end of the trail rather than inline with the crumb. */
  action?: (match: CrumbMatch<TData>) => ReactNode;
}

const crumbHandle = (handle: unknown) =>
  typeof handle === 'object' && handle !== null && 'crumb' in handle
    ? (handle as CrumbHandle)
    : undefined;

const trimSlash = (pathname: string) => pathname.replace(/\/$/, '');

export const BreadCrumb = () => {
  const { isMobile } = useSidebar();
  const matches = useMatches();

  // Collected before the filters below: an action belongs to the route that owns it
  // (and to the provider that route mounts), not to a crumb that survived them.
  const actions = matches
    .map((match) => ({ match, action: crumbHandle(match.handle)?.action }))
    .filter((entry) => entry.action);

  // A pathless route (Dashboard wrappers, errorElement boundaries) and an index route
  // share their parent's pathname: keeping the first match per pathname drops them
  // without comparing labels. The root's empty pathname goes the same way.
  const crumbs = matches
    .filter(
      (match, index, all) =>
        trimSlash(match.pathname) !== '' &&
        trimSlash(match.pathname) !== trimSlash(all[index - 1]?.pathname ?? ''),
    )
    // A narrow bar only has room for the scope. Filtered rather than hidden in CSS so
    // aria-current lands on a crumb that is actually rendered.
    .filter((match) => !isMobile || match.id === 'game');

  return (
    <header className="breadcrumb-bar h-12 w-full shrink-0 px-2 border-b-2 border-sidebar bg-background">
      <div className="bar-control flex items-center">
        <SidebarControl />
        <Separator orientation="vertical" className="bar-divider mx-2 h-6!" />
      </div>
      <nav
        aria-label="Breadcrumb"
        className="bar-crumbs flex min-w-0 items-center gap-2"
      >
        <ol className="flex min-w-0 items-center text-sm leading-none text-muted-foreground">
          {crumbs.map((match, index) => {
            const handle = crumbHandle(match.handle);
            const isLast = index === crumbs.length - 1;
            const to = handle?.to ?? match.pathname;
            const href = typeof to === 'function' ? to(match) : to;
            const label = handle ? (
              handle.crumb(match)
            ) : (
              // ponytail: raw URL segment for routes without a crumb, add handle.crumb to label one
              <span className="capitalize">
                {match.pathname.split('/').pop()}
              </span>
            );

            return (
              <li
                key={match.id}
                className={cn(
                  'inline-flex min-w-0 items-center h-6',
                  href === false && 'shrink-0',
                )}
              >
                {index > 0 && (
                  <span aria-hidden="true" className="px-2 shrink-0">
                    /
                  </span>
                )}
                {href === false ? (
                  label
                ) : isLast ? (
                  <span
                    aria-current="page"
                    className="truncate font-bold text-foreground"
                  >
                    {label}
                  </span>
                ) : (
                  <Link
                    to={href}
                    className="truncate hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
        {actions.map(({ match, action }) => (
          <span key={match.id} className="shrink-0">
            {action?.(match)}
          </span>
        ))}
      </nav>
      <div className="bar-auth">
        <AuthStatus />
      </div>
    </header>
  );
};
